import { useMemo, useRef, useState, useEffect } from "react";
import { MapContainer, TileLayer, ZoomControl } from "react-leaflet";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useSyncContainerTracking } from "@/hooks/api/use-shipments";
import { useSetting } from "@/hooks/api/use-settings";
import { toast } from "@/lib/toast-service";
import { getShipPosition, mapApiCurrentPositionToContainer } from "./world-map-utils";
import { useWorldMapData } from "./use-world-map-data";
import { useShipmentById } from "@/hooks/api/use-containers-recap";
import { useActiveIssuesCount } from "@/components/application/containers/container-issues-tab";
import { transformShipmentToContainer } from "@/utils/shipment-to-container";
import { parseUtcISO } from "@/utils/date-formatters";
import { FitBounds, MapResizeHandler, GlassDetailOverlay } from "./world-map-map-components";
import { WorldMapToolbar } from "./world-map-toolbar";
import { WorldMapListPanel } from "./world-map-list-panel";
import { WorldMapRoutesAndMarkers } from "./world-map-routes-and-markers";

export function ShipperContainersWorldMapPage() {
    const { t } = useTranslation(['pages:containers', 'common']);
    const location = useLocation();

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [deliveryFilter, setDeliveryFilter] = useState<"all" | "in_progress" | "delivered">("in_progress");

    // Refresh tracking
    const syncTrackingMutation = useSyncContainerTracking();
    const { data: refreshSetting } = useSetting('tracking', 'enable_tracking_refresh');
    const isRefreshEnabled = refreshSetting?.value === true;
    const [isRefreshingAll, setIsRefreshingAll] = useState(false);

    const {
        containers,
        filteredContainers,
        routes,
        progressById,
        lastUpdateTime,
        allPoints,
        tableRows,
    } = useWorldMapData({ searchTerm, deliveryFilter });

    const handleRefreshAllTracking = async () => {
        const containersToRefresh = containers.filter(c => c.containerNumber);
        if (containersToRefresh.length === 0) {
            toast.info(t('worldMap.noContainersToRefresh', { defaultValue: 'No containers with tracking data available to refresh' }));
            return;
        }
        setIsRefreshingAll(true);
        try {
            const results = await Promise.allSettled(
                containersToRefresh.map(c => syncTrackingMutation.mutateAsync(c.id))
            );
            let successCount = 0;
            let skippedCount = 0;
            let errorCount = 0;
            for (const r of results) {
                if (r.status === 'rejected') {
                    errorCount++;
                } else if (r.value.success) {
                    successCount++;
                } else if (r.value.skipped) {
                    skippedCount++;
                } else {
                    errorCount++;
                }
            }
            if (errorCount > 0) {
                toast.error(t('worldMap.refreshPartial', { defaultValue: `Refreshed ${successCount} of ${containersToRefresh.length} containers. ${errorCount} failed.` }));
            } else if (skippedCount > 0) {
                toast.info(t('worldMap.refreshWithSkipped', { defaultValue: `${successCount} refreshed, ${skippedCount} skipped (no BL number)` }));
            } else {
                toast.success(t('worldMap.refreshSuccess', { defaultValue: `Tracking refreshed for ${successCount} containers` }));
            }
        } catch {
            toast.error(t('worldMap.refreshError', { defaultValue: 'Failed to refresh tracking data' }));
        } finally {
            setIsRefreshingAll(false);
        }
    };

    const [isListPanelOpen, setIsListPanelOpen] = useState(false);
    const [hoveredContainerId, setHoveredContainerId] = useState<string | null>(null);
    const [highlightedRouteId, setHighlightedRouteId] = useState<string | null>(null);
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [selectedMarkerLatLng, setSelectedMarkerLatLng] = useState<[number, number] | null>(null);
    const [isDetailWindowOpen, setIsDetailWindowOpen] = useState(true);

    // Get selected container: prefer API data (same as detail page) for consistency; fallback to list data
    const { data: selectedShipmentData } = useShipmentById(selectedContainerId ?? undefined, !!selectedContainerId);
    const activeIssuesCount = useActiveIssuesCount(selectedContainerId ?? undefined);
    const selectedContainer = useMemo(() => {
        if (!selectedContainerId) return null;
        if (selectedShipmentData) return transformShipmentToContainer(selectedShipmentData);
        return filteredContainers.find(c => c.id === selectedContainerId) || null;
    }, [selectedContainerId, selectedShipmentData, filteredContainers]);

    /** Arricchisce il container del popup con recap (posizione+AIS, carrier/nave se il GET shipment è incompleto). */
    const selectedContainerForOverlay = useMemo(() => {
        if (!selectedContainer) return null;
        const row = tableRows.find((r) => r.container_id === selectedContainer.id);
        if (!row) return selectedContainer;

        const posFromTable =
            row.current_position != null ? mapApiCurrentPositionToContainer(row.current_position) : undefined;

        const merged: typeof selectedContainer = {
            ...selectedContainer,
            progress:
                row.progress_percentage != null
                    ? Math.max(0, Math.min(100, Number(row.progress_percentage) || 0))
                    : selectedContainer.progress,
            ...(posFromTable ? { currentPosition: posFromTable } : {}),
            shippingCompany: selectedContainer.shippingCompany ?? row.carrier_name ?? undefined,
            vesselName: selectedContainer.vesselName ?? row.vessel_name ?? undefined,
            voyageNumber: selectedContainer.voyageNumber ?? row.voyage_number ?? undefined,
            cargoDescription: selectedContainer.cargoDescription ?? row.cargo_description ?? undefined,
            cargoType: selectedContainer.cargoType ?? row.cargo_type ?? undefined,
            transportMode: selectedContainer.transportMode ?? row.transport_mode ?? undefined,
            originPortCode: selectedContainer.originPortCode ?? row.origin_port_unlocode ?? undefined,
            destinationPortCode: selectedContainer.destinationPortCode ?? row.destination_port_unlocode ?? undefined,
            estimatedDepartureDate:
                selectedContainer.estimatedDepartureDate ??
                (row.estimated_departure_date ? parseUtcISO(row.estimated_departure_date) ?? undefined : undefined),
            actualDepartureDate:
                selectedContainer.actualDepartureDate ??
                (row.actual_departure_date ? parseUtcISO(row.actual_departure_date) ?? undefined : undefined),
            documentCount: selectedContainer.documentCount ?? row.document_count ?? 0,
            emailCount: selectedContainer.emailCount ?? row.email_count ?? 0,
            alertCount: selectedContainer.alertCount ?? row.alert_count ?? 0,
            hasActiveAlerts: selectedContainer.hasActiveAlerts ?? row.has_active_alerts ?? false,
            documentStatusOverall:
                selectedContainer.documentStatusOverall ?? row.document_status_overall ?? undefined,
            exogenStatusOverall: selectedContainer.exogenStatusOverall ?? row.exogen_status_overall ?? undefined,
            commodity:
                selectedContainer.commodity && selectedContainer.commodity !== '—'
                    ? selectedContainer.commodity
                    : (row.cargo_description?.trim() || selectedContainer.commodity),
        };
        return merged;
    }, [selectedContainer, tableRows]);

    /** Timestamp tracking mostrato nel popup (non il max globale della lista). */
    const detailOverlayLastUpdate = useMemo(() => {
        if (!selectedContainerId) return null;
        const fromShipment = selectedShipmentData?.last_tracked_at
            ? parseUtcISO(selectedShipmentData.last_tracked_at)
            : null;
        if (fromShipment) return fromShipment;
        const row = tableRows.find((r) => r.container_id === selectedContainerId);
        const fromRowTracked = row?.last_tracked_at ? parseUtcISO(row.last_tracked_at) : null;
        if (fromRowTracked) return fromRowTracked;
        const fromRowUpdate = row?.last_update_at ? parseUtcISO(row.last_update_at) : null;
        return fromRowUpdate;
    }, [selectedContainerId, selectedShipmentData, tableRows]);

    const mapRef = useRef<HTMLDivElement | null>(null);

    const isLiveTrackingFullPage = location.pathname === '/shipper/live-tracking';

    const formatDateTime = (date: Date) => {
        return new Intl.DateTimeFormat(navigator.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    // Calcola altezza disponibile: viewport - header nav (72px) - page header (circa 80px)
    const mapHeight = useMemo(() => {
        return window.innerHeight - 72 - 80;
    }, []);

    // Reset tutti gli stati quando il pannello si chiude per ripristinare i colori
    useEffect(() => {
        if (!isListPanelOpen) {
            setSelectedContainerId(null);
            setHoveredContainerId(null);
            setHighlightedRouteId(null);
            setSelectedRouteId(null); // Nascondi route quando si chiude il pannello
        }
    }, [isListPanelOpen]);

    // Funzione helper per determinare se un container è attivo
    const isContainerActive = (containerId: string): boolean => {
        return hoveredContainerId === containerId ||
            selectedContainerId === containerId ||
            highlightedRouteId === containerId;
    };

    return (
        <>
            <div className={isLiveTrackingFullPage ? 'h-full flex flex-col' : ''}>
                {/* Wrapper principale con layout flex per effetto push */}
                <div
                    className={isLiveTrackingFullPage
                        ? 'flex flex-row gap-0 flex-1 min-h-0 relative'
                        : 'flex flex-row -mx-4 -my-6 sm:-mx-6 lg:-mx-8 gap-0 mt-2 relative'
                    }
                >
                    {/* Colonna: toolbar + (area mappa con pannello overlay) */}
                    <div
                        className="flex flex-col flex-1 transition-all duration-300 overflow-hidden"
                    >
                        <WorldMapToolbar
                            lastUpdateTime={lastUpdateTime}
                            formatDateTime={formatDateTime}
                            isRefreshEnabled={isRefreshEnabled}
                            isRefreshingAll={isRefreshingAll}
                            onRefresh={handleRefreshAllTracking}
                            isListPanelOpen={isListPanelOpen}
                            onShowContainers={() => setIsListPanelOpen(true)}
                            routesCount={routes.length}
                            deliveryFilter={deliveryFilter}
                            onDeliveryFilterChange={setDeliveryFilter}
                        />

                        {/* Area sotto toolbar: pannello overlay + mappa (flex così mapRef può espandersi) */}
                        <div
                            className="flex-1 min-h-0 relative flex flex-col"
                            style={!isLiveTrackingFullPage ? { minHeight: '70vh' } : undefined}
                        >
                            <WorldMapListPanel
                                isOpen={isListPanelOpen}
                                onClose={() => {
                                    setIsListPanelOpen(false);
                                    setHoveredContainerId(null);
                                    setSelectedContainerId(null);
                                    setHighlightedRouteId(null);
                                    setSelectedRouteId(null);
                                    setSelectedMarkerLatLng(null);
                                    setIsDetailWindowOpen(false);
                                }}
                                searchTerm={searchTerm}
                                onSearchChange={setSearchTerm}
                                routes={routes}
                                progressById={progressById}
                                hoveredContainerId={hoveredContainerId}
                                highlightedRouteId={highlightedRouteId}
                                onContainerClick={(route) => {
                                    const progress = progressById[route.c.id] ?? 0;
                                    const shipPos = route.c.currentPosition
                                        ? ([route.c.currentPosition.lat, route.c.currentPosition.lng] as [number, number])
                                        : getShipPosition(route.coordinates, progress);
                                    if (shipPos) setSelectedMarkerLatLng(shipPos);
                                    setSelectedContainerId(route.c.id);
                                    setHighlightedRouteId(route.c.id);
                                    setSelectedRouteId(route.c.id);
                                    setIsDetailWindowOpen(true);
                                }}
                                onContainerHover={setHoveredContainerId}
                            />

                            {/* Map Container - deve riempire l'area (flex-1 quando full page, 70vh altrimenti) */}
                            <div
                                ref={mapRef}
                                className={`relative w-full world-map-zoom-dark ${isLiveTrackingFullPage ? 'flex-1 min-h-0' : ''}`}
                                style={isLiveTrackingFullPage ? undefined : { height: '70vh', minHeight: '70vh' }}
                            >
                            {/* Empty state overlay - when no containers have valid positions */}
                            {routes.length === 0 && (
                                <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center bg-primary/80 backdrop-blur-sm">
                                    <div className="flex flex-col items-center gap-4 text-center p-6">
                                        <div className="rounded-full bg-secondary p-4">
                                            <svg className="size-12 text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-primary mb-1">{t('worldMap.emptyState.title', 'Nessun container con posizione')}</h3>
                                            <p className="text-sm text-tertiary max-w-xs">
                                                {t('worldMap.emptyState.description', 'I container in questo periodo non hanno ancora dati di tracciamento disponibili o non sono ancora partiti')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {mapHeight > 0 && (
                                <MapContainer
                                    style={{ height: "100%", width: "100%" }}
                                    center={[20, 0]}
                                    zoom={2}
                                    zoomControl={false}
                                    worldCopyJump
                                    scrollWheelZoom={false}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                        subdomains="abcd"
                                    />
                                    <ZoomControl position="bottomright" />
                                    <GlassDetailOverlay
                                        selectedContainer={selectedContainerForOverlay}
                                        selectedMarkerLatLng={selectedMarkerLatLng}
                                        lastUpdateTime={detailOverlayLastUpdate ?? null}
                                        formatDateTime={formatDateTime}
                                        isOpen={isDetailWindowOpen}
                                        activeIssuesCount={selectedContainerId ? activeIssuesCount : undefined}
                                        onClose={() => {
                                            setSelectedContainerId(null);
                                            setHighlightedRouteId(null);
                                            setSelectedRouteId(null);
                                            setSelectedMarkerLatLng(null);
                                        }}
                                        onCollapse={() => setIsDetailWindowOpen(false)}
                                        collapseTooltip={t('worldMap.hideToSeeRoute', 'Hide to see route')}
                                    />

                                    <WorldMapRoutesAndMarkers
                                        routes={routes}
                                        progressById={progressById}
                                        selectedRouteId={selectedRouteId}
                                        isContainerActive={isContainerActive}
                                        onMarkerClick={(containerId, position) => {
                                            setSelectedContainerId(containerId);
                                            setHighlightedRouteId(containerId);
                                            setSelectedRouteId(containerId);
                                            setSelectedMarkerLatLng(position);
                                            setIsDetailWindowOpen(true);
                                        }}
                                    />

                                    <FitBounds
                                        points={allPoints}
                                        selectedContainerId={selectedContainerId}
                                        routes={routes}
                                        isPanelOpen={isListPanelOpen}
                                    />
                                    <MapResizeHandler wrapperRef={mapRef} isPanelOpen={isListPanelOpen} />
                                </MapContainer>
                            )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </>
    );
}

export default ShipperContainersWorldMapPage;
